import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { isHalloweenAndEarlyBirdRetired } from '../../utils/openingHoursUtils';

const STORAGE_KEY = 'earlyBirdChristmasTicketsPopup2026';
const IMAGE_SRC = 'https://BelvoirCastle.b-cdn.net/Christmas/Screenshot%202026-10-27%20at%2015.20.46.png';
const TICKETS_PATH = '/christmas/tickets';

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(8, 14, 28, 0.78);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  animation: fadeIn 0.3s ease-out forwards;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const ModalContent = styled.div`
  background: #101d40;
  border-radius: 16px;
  max-width: 760px;
  width: 100%;
  position: relative;
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.45);
  overflow: hidden;
  animation: slideIn 0.35s ease-out forwards;

  @keyframes slideIn {
    from {
      transform: scale(0.92) translateY(-16px);
      opacity: 0;
    }
    to {
      transform: scale(1) translateY(0);
      opacity: 1;
    }
  }

  @media (max-width: 768px) {
    border-radius: 12px;
    max-height: 92vh;
    overflow-y: auto;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  background: rgba(16, 29, 64, 0.82);
  border: none;
  color: #fff;
  cursor: pointer;
  font-size: 1.1rem;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);

  &:hover {
    background: #101d40;
  }
`;

const BannerLink = styled(Link)`
  display: block;
  line-height: 0;

  img {
    width: 100%;
    height: auto;
    display: block;
  }
`;

const Content = styled.div`
  padding: 1.35rem 1.75rem 1.6rem;
  text-align: center;

  @media (max-width: 768px) {
    padding: 1.1rem 1.15rem 1.25rem;
  }
`;

const Title = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.85rem;
  font-weight: 600;
  color: #f3e6c4;
  margin: 0 0 1rem;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 1.45rem;
  }
`;

const BookButton = styled(Link)`
  display: inline-block;
  background: #f3e6c4;
  color: #101d40;
  text-decoration: none;
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.05rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 0.85rem 1.75rem;
  border-radius: 8px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 18px rgba(0, 0, 0, 0.25);
  }
`;

const EarlyBirdChristmasPopup = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isHalloweenAndEarlyBirdRetired()) return undefined;
    if (sessionStorage.getItem(STORAGE_KEY)) return undefined;

    const showTimer = setTimeout(() => {
      setIsVisible(true);
    }, 1200);

    return () => clearTimeout(showTimer);
  }, []);

  useEffect(() => {
    if (!isVisible) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsVisible(false);
        sessionStorage.setItem(STORAGE_KEY, 'true');
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isVisible]);

  const handleClose = () => {
    setIsVisible(false);
    sessionStorage.setItem(STORAGE_KEY, 'true');
  };

  if (!isVisible || isHalloweenAndEarlyBirdRetired()) return null;

  return (
    <ModalOverlay onClick={handleClose}>
      <ModalContent
        role="dialog"
        aria-modal="true"
        aria-labelledby="early-bird-christmas-title"
        onClick={(event) => event.stopPropagation()}
      >
        <CloseButton type="button" onClick={handleClose} aria-label="Close">
          <FontAwesomeIcon icon={faTimes} />
        </CloseButton>
        <BannerLink to={TICKETS_PATH} onClick={handleClose} aria-label="Book early bird Christmas tickets">
          <img
            src={IMAGE_SRC}
            alt="Where Christmas magic begins. Early bird tickets at Belvoir Castle."
          />
        </BannerLink>
        <Content>
          <Title id="early-bird-christmas-title">
            Last chance for early bird Christmas tickets
          </Title>
          <BookButton to={TICKETS_PATH} onClick={handleClose}>
            Book tickets
          </BookButton>
        </Content>
      </ModalContent>
    </ModalOverlay>
  );
};

export default EarlyBirdChristmasPopup;
